import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HardHat, ArrowLeft, Shield } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const H2 = ({ children, id }) => (
  <h2 id={id} className="text-xl font-bold text-slate-900 mt-10 mb-4 scroll-mt-24">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-lg font-bold text-slate-800 mt-6 mb-3">{children}</h3>
);

const PrivacyPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Privacy Policy"
        description="ContractorsLab Privacy Policy — how we collect, use, and protect your personal information. GDPR, CCPA, and state-law compliant."
        path="/privacy"
      />
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <Link to="/" className="text-slate-400 hover:text-white flex items-center gap-2 text-sm">
                <ArrowLeft className="w-4 h-4" /> {t('legal.backHome')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <article className="prose prose-slate max-w-none">
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-2">{t('legal.privacy')}</h1>
          <p className="text-slate-500 mb-8">{t('legal.lastUpdated')}: January 19, 2026 · Effective: February 1, 2026</p>

          {/* Summary card */}
          <div className="bg-slate-100 border-l-4 border-slate-400 p-5 mb-8 rounded-sm">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-slate-700 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-slate-900 mb-1">At-a-glance summary</p>
                <p className="text-sm text-slate-700 leading-relaxed m-0">
                  We collect information you provide (account, job, and payment data), information we
                  generate (logs, device data, usage), and information from partners (identity
                  verification, fraud prevention). We use it to run the marketplace, match you with the
                  right pros or clients, process payments, and keep everyone safe. We do <strong>not</strong>{' '}
                  sell your personal information. You have extensive rights to access, correct, delete,
                  or export your data — see Sections 10–12.
                </p>
              </div>
            </div>
          </div>

          <p className="lead text-lg text-slate-700">
            This Privacy Policy ("Policy") describes how <strong>Retention Logic LLC</strong>, the
            company that creates, owns, and operates ContractorsLab.com (together with its affiliates,
            "ContractorsLab," "we," "our," or "us"), collects, uses, discloses, retains, and secures
            personal information about Users of the ContractorsLab.com website, applications, and
            services (collectively, the "Service"). It also describes the rights you have with respect
            to your personal information under applicable laws, including the California Consumer
            Privacy Act of 2018 as amended by the California Privacy Rights Act ("CCPA/CPRA"), the
            Virginia Consumer Data Protection Act ("VCDPA"), Colorado Privacy Act ("CPA"), Connecticut
            Data Privacy Act ("CTDPA"), Utah Consumer Privacy Act ("UCPA"), Texas Data Privacy and
            Security Act ("TDPSA"), and — where applicable to non-U.S. Users — the EU General Data
            Protection Regulation ("GDPR") and UK GDPR.
          </p>

          <H2>1. Who We Are and How to Contact Us</H2>
          <p>
            <strong>Retention Logic LLC</strong> is the operator of the ContractorsLab Service and is
            the "business" (under CCPA/CPRA), "controller" (under GDPR/state laws), or equivalent with
            respect to the personal information described in this Policy. To reach us about privacy
            matters, contact us through our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link> and select
            the <strong>Legal / Compliance</strong> department.
          </p>
          <p>
            <strong>EU/UK Representative:</strong> Users in the European Economic Area or United Kingdom
            may contact our designated Article 27 representative through the same Contact page (select
            "Legal / Compliance"). We will forward the inquiry promptly.
          </p>

          <H2>2. Scope of This Policy</H2>
          <p>
            This Policy applies to personal information we process in connection with:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>The websites, web applications, and mobile applications at or linked from contractorslab.com.</li>
            <li>In-app messaging, Lead matching, Wallet transactions, and document uploads.</li>
            <li>Our marketing and communication channels (email, SMS where opted-in, paid advertising).</li>
            <li>Offline interactions (e.g., customer support calls, if ever recorded with consent).</li>
          </ul>
          <p>
            It does <strong>not</strong> apply to personal information processed by Contractors directly
            after contact reveal, by payment processors under their own privacy policies, or by
            third-party websites linked from our Service.
          </p>

          <H2>3. Personal Information We Collect</H2>
          <H3>3.1 Information You Provide Directly</H3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account data:</strong> name, email, phone (optional), hashed password, role (Client / Contractor / Admin), date of account creation, language preference.</li>
            <li><strong>Profile data:</strong> for Contractors — company name, license number(s), license jurisdiction, insurance certificate, EIN or SSN (where required for 1099 reporting), business address, service categories, service zip code and radius, headshot or company logo.</li>
            <li><strong>Job data:</strong> for Clients — project descriptions, property zip code, photos, budgets, preferred timelines.</li>
            <li><strong>Transactional data:</strong> Wallet top-ups, Contact Reveal Fee debits, Wallet balance, refund history (payment-card data is handled directly by Stripe; see Section 6).</li>
            <li><strong>Communications:</strong> in-app messages, contact-reveal requests, support tickets, customer-service call transcripts (where permitted by law and disclosed).</li>
            <li><strong>Verification documents:</strong> license certificates, insurance COIs, government-issued ID (for contractor identity verification).</li>
            <li><strong>Reviews and ratings:</strong> written reviews, star ratings, questionnaire responses, "would hire again" flags.</li>
          </ul>

          <H3>3.2 Information We Collect Automatically</H3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Device and connection:</strong> IP address, browser type and version, operating system, mobile device identifiers (IDFA/GAID), screen resolution, referring/exit URLs, language setting.</li>
            <li><strong>Usage logs:</strong> pages viewed, features used, search queries, click paths, time on page, errors encountered, crash reports.</li>
            <li><strong>Location:</strong> approximate location derived from IP address; we do not collect precise GPS unless you explicitly grant permission on a mobile device.</li>
            <li><strong>Cookies and similar technologies:</strong> see Section 7.</li>
          </ul>

          <H3>3.3 Information from Third Parties</H3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Identity verification providers</strong> (where used): verification outcomes, license-status lookups, insurance COI validity.</li>
            <li><strong>OAuth providers</strong> (Google via Emergent Authentication): basic profile fields such as name, email, profile picture, locale — when you use "Continue with Google."</li>
            <li><strong>Payment processor (Stripe):</strong> transaction status, last four digits of card, card brand, risk signals — never full card numbers or CVV.</li>
            <li><strong>Analytics and marketing partners:</strong> attribution data, campaign performance, aggregated usage statistics.</li>
            <li><strong>Public records and state license databases:</strong> license status, expiration, disciplinary history (for Contractor verification).</li>
            <li><strong>Fraud-prevention services:</strong> device reputation scores, suspected-fraud signals.</li>
          </ul>

          <H3>3.4 Sensitive Information</H3>
          <p>
            We treat the following as "sensitive personal information" under CCPA/CPRA and equivalent
            laws: government-issued ID numbers (SSN/EIN, driver's license), precise geolocation (only
            when explicitly granted), account credentials (password, MFA seeds), and payment account
            numbers (processed by Stripe). We use sensitive information only as reasonably necessary to
            operate the Service and do not use it for inferring characteristics about you.
          </p>

          <H3>3.5 Information About Children</H3>
          <p>
            The Service is not directed to individuals under 18 years of age. We do not knowingly collect
            personal information from children. If we learn that we have inadvertently collected personal
            information from a child, we will delete it promptly.
          </p>

          <H2>4. How We Use Personal Information</H2>
          <p>We use personal information for the following purposes, each mapped below to the GDPR legal bases:</p>
          <div className="overflow-x-auto my-4">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left p-3 border border-slate-200">Purpose</th>
                  <th className="text-left p-3 border border-slate-200">Data used</th>
                  <th className="text-left p-3 border border-slate-200">GDPR legal basis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-3 border border-slate-200">Provide the Service (Lead matching, messaging, Wallet)</td>
                  <td className="p-3 border border-slate-200">Account, profile, job, usage</td>
                  <td className="p-3 border border-slate-200">Contract performance</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">Verify Contractor credentials</td>
                  <td className="p-3 border border-slate-200">License, insurance, ID</td>
                  <td className="p-3 border border-slate-200">Contract + legitimate interests</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">Process payments and refunds</td>
                  <td className="p-3 border border-slate-200">Transaction, Stripe tokens</td>
                  <td className="p-3 border border-slate-200">Contract performance</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">Prevent fraud, abuse, and security threats</td>
                  <td className="p-3 border border-slate-200">Device, usage, transaction signals</td>
                  <td className="p-3 border border-slate-200">Legitimate interests + legal obligation</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">Send transactional communications</td>
                  <td className="p-3 border border-slate-200">Account, contact</td>
                  <td className="p-3 border border-slate-200">Contract performance</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">Send marketing (with opt-in)</td>
                  <td className="p-3 border border-slate-200">Contact, preferences</td>
                  <td className="p-3 border border-slate-200">Consent</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">Comply with tax, legal, and regulatory obligations</td>
                  <td className="p-3 border border-slate-200">Identity, transaction</td>
                  <td className="p-3 border border-slate-200">Legal obligation</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">Improve and personalize the Service</td>
                  <td className="p-3 border border-slate-200">Usage, feedback</td>
                  <td className="p-3 border border-slate-200">Legitimate interests</td>
                </tr>
                <tr>
                  <td className="p-3 border border-slate-200">Enforce our Terms, protect rights</td>
                  <td className="p-3 border border-slate-200">As necessary</td>
                  <td className="p-3 border border-slate-200">Legitimate interests + legal obligation</td>
                </tr>
              </tbody>
            </table>
          </div>

          <H3>4.1 Automated Decision-Making</H3>
          <p>
            We use algorithmic Lead matching to select up to three Contractors per job based on category,
            location, quality score, Wallet readiness, and rotation fairness. Matching is not a
            "significant automated decision" under GDPR Article 22 because it does not produce a legal
            effect or similarly significant impact — every Client retains the final decision to select,
            reject, or repost their job. Users may request human review of any match outcome through our
            Contact page.
          </p>

          <H3>4.2 Artificial Intelligence and Training Data</H3>
          <p>
            We use third-party large-language-model providers (currently OpenAI, Anthropic, and Google via
            the Emergent integration platform) solely to generate educational tips content. Your personal
            information is <strong>not</strong> sent to these providers to generate tips. Where we ever
            expand AI features (for example, AI-assisted messaging summaries), we will provide additional
            notice and, where required, obtain consent.
          </p>

          <H2>5. Disclosure of Personal Information</H2>
          <p>
            We disclose personal information only as described in this Policy. We do <strong>not sell</strong>{' '}
            personal information for monetary consideration, and we do <strong>not share</strong> personal
            information for cross-context behavioral advertising as those terms are defined by
            CCPA/CPRA.
          </p>

          <H3>5.1 To Other Users</H3>
          <p>
            Certain information is made available to other Users as part of the core Service: Client job
            details (excluding contact info) are visible to matched Contractors; Contractor profile
            details (business name, license, rating, reviews) are visible to Clients; and contact
            information is exchanged only upon mutual consent after Client selection.
          </p>

          <H3>5.2 To Service Providers (Processors)</H3>
          <p>
            We use the following categories of service providers, each bound by contracts that restrict
            their use of personal information to the services they provide to us:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Cloud hosting and databases:</strong> our infrastructure providers host the Service, databases, and backups.</li>
            <li><strong>Payment processing:</strong> Stripe, Inc. (stripe.com/privacy) processes Wallet top-ups and fee debits.</li>
            <li><strong>Document storage:</strong> Amazon Web Services (AWS) S3 securely stores Contractor license/insurance uploads.</li>
            <li><strong>Email and SMS delivery:</strong> providers such as SparkPost (email) and Infobip (SMS) transmit transactional and opted-in marketing messages.</li>
            <li><strong>Analytics:</strong> privacy-conscious analytics tools help us understand aggregated usage.</li>
            <li><strong>Customer support:</strong> help-desk platforms store support tickets.</li>
            <li><strong>Identity/license verification:</strong> state-license-lookup and fraud-prevention services.</li>
            <li><strong>AI educational content:</strong> LLM providers as described in Section 4.2.</li>
          </ul>

          <H3>5.3 Legal and Safety Disclosures</H3>
          <p>
            We may disclose personal information to law enforcement, regulators, courts, or other
            third parties where we reasonably believe it is necessary to: (a) comply with applicable law,
            subpoena, or court order; (b) protect our rights, property, or safety, or the rights,
            property, or safety of our Users or the public; (c) detect, prevent, or address fraud,
            security, or technical issues; or (d) respond to an emergency.
          </p>

          <H3>5.4 Corporate Transactions</H3>
          <p>
            If ContractorsLab is involved in a merger, acquisition, reorganization, financing, bankruptcy,
            or sale of all or a portion of its assets, personal information may be transferred to the
            successor entity, subject to this Policy or a successor policy that is substantially
            equivalent. We will notify Users in advance where feasible.
          </p>

          <H3>5.5 Aggregated or De-Identified Data</H3>
          <p>
            We may share aggregated, anonymized, or de-identified information that cannot reasonably be
            used to identify you with partners, researchers, or the public for any lawful purpose. We
            commit to maintaining and using such information only in de-identified form and not to
            re-identify it.
          </p>

          <H2>6. Payment Processing</H2>
          <p>
            Payment-card details (PAN, CVV, expiration) are collected and processed directly by Stripe,
            Inc. via Stripe Elements or Stripe Checkout and do not pass through or persist on our
            servers. Stripe is a PCI-DSS Level 1 certified processor. We receive only tokens, the last
            four digits, card brand, authorization outcomes, and risk signals. See Stripe's privacy
            policy at stripe.com/privacy.
          </p>

          <H2>7. Cookies and Tracking Technologies</H2>
          <H3>7.1 Categories</H3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Strictly necessary:</strong> required for authentication, CSRF protection, session continuity. Cannot be disabled.</li>
            <li><strong>Functional:</strong> remember preferences (language, dark/light mode, recently viewed jobs).</li>
            <li><strong>Analytics:</strong> privacy-conscious first-party analytics for understanding usage.</li>
            <li><strong>Marketing/attribution:</strong> measure the effectiveness of referral and paid-ad campaigns, with your consent where required.</li>
          </ul>
          <H3>7.2 Managing Preferences</H3>
          <p>
            You can manage cookie preferences through our cookie banner (when shown), your browser
            settings, or device-level controls. Disabling strictly necessary cookies may break parts of
            the Service.
          </p>
          <H3>7.3 Do Not Track / Global Privacy Control</H3>
          <p>
            Our Service honors the Global Privacy Control (GPC) browser signal for Users in states
            recognizing GPC as an opt-out preference (currently California, Colorado, Connecticut). We do
            not respond to "Do Not Track" headers because there is no industry consensus on how they
            should be interpreted.
          </p>

          <H2>8. Data Retention</H2>
          <p>We retain personal information only for as long as necessary to fulfill the purposes in this Policy, subject to minimums required by law:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Account data:</strong> for the life of your Account, plus seven (7) years after deletion for tax, accounting, and legal-hold purposes.</li>
            <li><strong>Transactional data:</strong> seven (7) years to comply with financial recordkeeping and tax law.</li>
            <li><strong>Job and Lead data:</strong> indefinitely in de-identified form for platform analytics; identifiable form removed one (1) year after account closure unless subject to legal hold.</li>
            <li><strong>Messages:</strong> three (3) years after last activity, then archived in de-identified form.</li>
            <li><strong>Reviews:</strong> retained indefinitely to preserve historical accuracy; reviewer name may be anonymized on request.</li>
            <li><strong>Logs and security data:</strong> up to twenty-four (24) months for fraud-prevention and security investigations.</li>
            <li><strong>Marketing preferences:</strong> until you withdraw consent, plus record-keeping evidence of withdrawal.</li>
          </ul>
          <p>Longer retention may apply if required by law, a legal hold, or ongoing dispute.</p>

          <H2>9. Data Security</H2>
          <p>
            We implement reasonable administrative, technical, and physical safeguards designed to
            protect personal information against unauthorized access, alteration, disclosure, or
            destruction. Examples include encryption in transit (TLS 1.2+) and at rest, least-privilege
            access controls, multi-factor authentication for administrators, encrypted backups, secure
            cloud infrastructure, vulnerability scanning, and periodic security reviews. Despite our
            safeguards, no system is 100% secure. In the event of a confirmed breach that poses a risk
            to your rights, we will notify affected Users and regulators as required by applicable law.
          </p>

          <H2 id="your-rights">10. Your Privacy Rights (U.S. States)</H2>
          <p>
            <strong>California residents:</strong> please also review our dedicated{' '}
            <Link to="/california-privacy" className="text-orange-600 font-medium">California Consumer Privacy Notice</Link>,
            which supplements this Policy with category-level disclosures, a detailed description of your
            CCPA/CPRA rights, and instructions for submitting a Right to Know, Delete, Correct, Port,
            Opt Out, or Limit request.
          </p>
          <p>
            If you are a resident of California, Virginia, Colorado, Connecticut, Utah, Texas, Oregon,
            Delaware, or any other state that grants similar rights, you may have the following rights
            with respect to your personal information, subject to certain exceptions:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Right to know / access:</strong> receive confirmation of whether we process your data and a copy of the data.</li>
            <li><strong>Right to delete:</strong> request deletion of your data, subject to legal retention requirements.</li>
            <li><strong>Right to correct:</strong> request that we correct inaccurate personal information.</li>
            <li><strong>Right to portability:</strong> receive your data in a structured, machine-readable format.</li>
            <li><strong>Right to opt out of sale/sharing:</strong> we do not sell or share in the statutory sense; this right is preserved regardless.</li>
            <li><strong>Right to limit use of sensitive information:</strong> request limits on our use of your sensitive data.</li>
            <li><strong>Right to appeal:</strong> appeal any denial of a rights request; we will respond within 60 days.</li>
            <li><strong>Right to non-discrimination:</strong> we will not deny, charge differently for, or provide a different quality of Service because you exercised your rights.</li>
          </ul>

          <H3>10.1 How to Exercise Rights</H3>
          <p>
            Submit a request through our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>{' '}
            and select <strong>Legal / Compliance</strong>. We will verify your identity (for example,
            by confirming access to your Account email) before fulfilling the request. We respond within
            forty-five (45) days for CCPA/CPRA and other state-law requests; extensions up to a further
            forty-five (45) days may apply in complex cases with notice to you. There is no fee for a
            first request in any twelve-month period.
          </p>

          <H3>10.2 Authorized Agents</H3>
          <p>
            You may designate an authorized agent to make requests on your behalf. We require proof of
            the agent's authority (signed authorization) and of your identity.
          </p>

          <H3>10.3 Shine the Light (California)</H3>
          <p>
            California residents may request an accounting of any disclosures of certain personal
            information to third parties for those third parties' direct-marketing purposes. We do not
            share personal information in this manner, and we will confirm that upon request.
          </p>

          <H2>11. Your Rights Under GDPR / UK GDPR</H2>
          <p>
            If you are located in the European Economic Area, the United Kingdom, or Switzerland, you
            have additional rights under GDPR:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Right of access (Article 15).</li>
            <li>Right to rectification (Article 16).</li>
            <li>Right to erasure / "right to be forgotten" (Article 17).</li>
            <li>Right to restriction of processing (Article 18).</li>
            <li>Right to data portability (Article 20).</li>
            <li>Right to object, including to direct marketing (Article 21).</li>
            <li>Rights concerning automated decision-making (Article 22).</li>
            <li>Right to withdraw consent at any time, without affecting the lawfulness of prior consent-based processing.</li>
            <li>Right to lodge a complaint with your local Data Protection Authority.</li>
          </ul>

          <H2>12. International Data Transfers</H2>
          <p>
            ContractorsLab is operated from the United States. If you access the Service from outside the
            U.S., your personal information will be transferred to, stored, and processed in the U.S. and
            potentially in other jurisdictions where our service providers operate. We rely on the
            following transfer mechanisms where applicable:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>EU-U.S., UK-U.S., and Swiss-U.S. Data Privacy Framework participation (where we or our processors certify).</li>
            <li>Standard Contractual Clauses (SCCs) adopted by the European Commission and the UK International Data Transfer Addendum.</li>
            <li>Your explicit consent, where appropriate.</li>
          </ul>

          <H2>13. Children's Privacy (COPPA)</H2>
          <p>
            We do not knowingly collect personal information from children under 13. If you believe we
            have collected information from a child under 13, please contact us and we will delete it.
          </p>

          <H2>14. Third-Party Websites and Services</H2>
          <p>
            Our Service contains links to third-party websites and integrates with third-party services.
            We are not responsible for the privacy practices of those third parties. We encourage you to
            review their privacy policies before providing any personal information.
          </p>

          <H2>15. Changes to This Policy</H2>
          <p>
            We may update this Policy from time to time. When we make material changes, we will notify
            you by email (to the address associated with your Account) or by prominent in-app notice, at
            least thirty (30) days before the changes take effect (except for changes required by law,
            which may take effect immediately). We will also update the "Last Updated" date at the top of
            this Policy. Your continued use of the Service after the effective date constitutes
            acceptance.
          </p>

          <H2>16. State-Specific Disclosures</H2>
          <H3>16.1 Nevada Residents</H3>
          <p>
            Under Nevada Revised Statutes § 603A.340, Nevada residents may request that we not sell their
            personal information. We do not sell personal information; this right is preserved
            regardless.
          </p>
          <H3>16.2 Illinois Biometric Information Privacy Act (BIPA)</H3>
          <p>
            We do not collect, capture, receive, or store biometric identifiers or biometric information
            as defined by BIPA. If this ever changes, we will provide the disclosures and written consent
            BIPA requires before any collection.
          </p>
          <H3>16.3 New York SHIELD Act</H3>
          <p>
            For New York residents, we implement administrative, technical, and physical safeguards
            reasonably designed to protect private information as required by the New York SHIELD Act.
          </p>

          <H2>17. Accessibility</H2>
          <p>
            This Policy is available in accessible formats. If you need assistance reading or
            understanding this Policy, please contact us through our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>.
          </p>

          <H2>18. Language</H2>
          <p>
            This Policy is drafted in English. Any translation is provided for convenience. In case of
            conflict, the English version controls.
          </p>

          <H2>19. Governing Law and Jurisdiction</H2>
          <p>
            This Policy is governed by and construed in accordance with the laws of the
            <strong> Commonwealth of Puerto Rico</strong> and applicable U.S. federal laws (including
            privacy statutes such as the CCPA/CPRA, COPPA, GLBA, and HIPAA where applicable), without
            regard to conflict-of-laws principles. Any dispute arising from or related to this Policy
            that is not subject to arbitration under our{' '}
            <Link to="/terms" className="text-orange-600 font-medium">Terms of Service</Link> shall be
            brought exclusively in the Commonwealth Court of First Instance of Puerto Rico (Tribunal
            de Primera Instancia) or the U.S. District Court for the District of Puerto Rico, both
            sitting in San Juan, Puerto Rico. Nothing in this Section limits any non-waivable
            statutory rights you may have under the privacy laws of your state, province, or country
            of residence.
          </p>

          <H2>20. Contact &amp; Complaints</H2>
          <p>
            For any privacy-related questions, requests, or complaints, reach us through our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link> and select
            <strong> Legal / Compliance</strong>. EU/UK Users may also contact their local Data
            Protection Authority.
          </p>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              By using ContractorsLab, you acknowledge that you have read and understood this Privacy
              Policy and the ways we process your personal information as described above.
            </p>
          </div>
        </article>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="font-black text-white">CONTRACTORSLAB</span>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/california-privacy" className="hover:text-white">CA Privacy</Link>
              <Link to="/sms-terms" className="hover:text-white">SMS Terms</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
